/**
 * PackageCard — kartu satu paket point di halaman top-up (plan 19 Fase 1).
 *
 * Menampilkan: jumlah point besar, harga IDR, harga per point, dan tombol
 * beli. Presentational murni — flow pembayaran ditangani parent screen.
 */

import { Pressable, StyleSheet, View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { Brand, Colors, Radii, Spacing } from '@/constants/theme';
import { formatIdr } from '@/lib/billing-format';
import type { CreditPackage } from '@/lib/api/billing';

interface PackageCardProps {
  pkg: CreditPackage;
  /** true saat ada pembelian lain yang sedang diproses. */
  disabled: boolean;
  /** true hanya untuk paket yang dipilih dan sedang diproses. */
  isProcessing: boolean;
  onBuy: (packageCode: string) => void;
}

export function PackageCard({ pkg, disabled, isProcessing, onBuy }: PackageCardProps) {
  const perCredit = Math.round(pkg.price_idr / pkg.credit_amount);
  return (
    <View style={styles.card}>
      <ThemedText style={styles.amount}>{pkg.credit_amount}</ThemedText>
      <ThemedText style={styles.creditLabel}>point</ThemedText>
      <ThemedText style={styles.price}>{formatIdr(pkg.price_idr)}</ThemedText>
      <ThemedText themeColor="textSecondary" style={styles.perCredit}>
        {formatIdr(perCredit)} per point
      </ThemedText>
      <Pressable
        onPress={() => onBuy(pkg.code)}
        disabled={disabled}
        style={({ pressed }) => [
          styles.buyBtn,
          (pressed || disabled) && styles.buyBtnPressed,
        ]}
        accessibilityRole="button"
        accessibilityLabel={`Beli ${pkg.label}`}
      >
        <ThemedText style={styles.buyBtnText}>
          {isProcessing ? 'Memproses…' : 'Beli'}
        </ThemedText>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#ffffff',
    borderColor: '#e5e5e5',
    borderRadius: Radii.medium,
    borderWidth: 1,
    gap: Spacing.one,
    padding: Spacing.three,
  },
  amount: { color: Brand.primary, fontSize: 32, fontWeight: '700', lineHeight: 36 },
  creditLabel: { color: Brand.primary, fontSize: 13, fontWeight: '700' },
  price: { color: Colors.light.text, fontSize: 17, fontWeight: '700', marginTop: Spacing.two },
  perCredit: { fontSize: 12, lineHeight: 18 },
  buyBtn: {
    alignItems: 'center',
    backgroundColor: Brand.primary,
    borderRadius: Radii.small,
    marginTop: Spacing.two,
    paddingVertical: 12,
  },
  buyBtnPressed: { opacity: 0.7 },
  buyBtnText: { color: '#ffffff', fontSize: 14, fontWeight: '700' },
});
